import React, { useState } from 'react';
import { Button, Card, FlexRow, FormCell, Grid, Header2Text } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { AddDefaultContactForm } from '../../components/clients/AddDefaultContactForm';
import { IAddEditDefaultContactData } from '../../types/clients/CreateDefaultContact';
import {
  getListOfPresentationDefaultContacts,
  IDefaultContactData,
  IPresentationDefaultContact,
} from '../../types/clients/IDefaultContact';
import { DefaultContactsTable } from '../../components/clients/DefaultContactsTable';
import { EditDefaultContactForm } from '../../components/clients/EditDefaultContactForm';
import { useListRolesQuery } from '../../API/roles/RolesApi';
import { Show } from '../../components/shared/Show';
import { useListPagedAdminUsersQuery } from '../../API/administration/AdminUsersAPI';
import { eAdminAccountStatus } from '../../types/administration/userAdministration/IAdminUser';
import { convertToError } from '../../API/BaseApi';

interface IDefaultContactsPageProps {
  addFetchError?: Error;
  addFetchStatus: IFetchStatus;
  deleteFetchError?: Error;
  deleteFetchStatus: IFetchStatus;
  affectedOrderFetchStatus: IFetchStatus;
  defaultContacts: IDefaultContactData[];
  loading: boolean;
  updateFetchError?: Error;
  updateFetchStatus: IFetchStatus;
  onResetAdd: () => void;
  onResetUpdate: () => void;
  onAddUpdateDefaultContact: (contact: IAddEditDefaultContactData) => void;
  getAffectedOrders: (contact: IAddEditDefaultContactData) => void;
  clientId: string;
  affectedOrders?: number;
}

export const DefaultContactsPage: React.FC<IDefaultContactsPageProps> = ({
                                                                           addFetchError,
                                                                           addFetchStatus,
                                                                           deleteFetchError,
                                                                           deleteFetchStatus,
                                                                           defaultContacts,
                                                                           loading,
                                                                           updateFetchError,
                                                                           updateFetchStatus,
                                                                           onResetAdd,
                                                                           onResetUpdate,
                                                                           onAddUpdateDefaultContact,
                                                                           getAffectedOrders,
                                                                           affectedOrderFetchStatus,
                                                                           clientId,
                                                                           affectedOrders,
                                                                         }) => {
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [editContact, setEditContact] = useState<IPresentationDefaultContact | null>(null);
  const { data: roles = [] } = useListRolesQuery();
  const { error: PagedAdminUsersError } = useListPagedAdminUsersQuery({
    status: eAdminAccountStatus.Active,
    fetchAll: true,
  });

  const onAddAdminClick = () => {
    setShowAddContactForm(true);
  };
  const onCancelAddContactForm = () => {
    setShowAddContactForm((showAddContactForm) => !showAddContactForm);
    onResetAdd();
  };
  const onCancelEditContactForm = () => {
    setEditContact(null);
    onResetUpdate();
  };

  const rolesOrdered: string[] = roles.map((role) => role.name);
  const presentationData: IPresentationDefaultContact[] = getListOfPresentationDefaultContacts(
    defaultContacts,
  ).sort((a, b) => rolesOrdered.indexOf(a.role) - rolesOrdered.indexOf(b.role));

  const onSelectAdmin = (adminId: string) => {
    const existingContact = presentationData.find((contact) => contact.admin.id === adminId);
    if (existingContact) {
      onCancelAddContactForm();
      setEditContact(existingContact);
    }
  };

  return (
    <Card>
      <Grid>
        <FlexRow justifyContent={'space-between'} width={100}>
          <FormCell hintText={<>Spruce team members responsible for fulfilling this client's orders.</>}>
            <Header2Text>Default Contacts</Header2Text>
          </FormCell>
          <Button intent={'secondary'} onClick={onAddAdminClick}>
            <FontAwesomeIcon icon={faUserPlus} /> Add New Contact
          </Button>
        </FlexRow>
        <Show when={!!roles.length}>
          <DefaultContactsTable
            data={presentationData}
            loading={loading}
            onEditContact={setEditContact}
            roles={roles}
          />
        </Show>
      </Grid>
      {showAddContactForm && (
        <AddDefaultContactForm
          onSubmit={onAddUpdateDefaultContact}
          onCancel={onCancelAddContactForm}
          addFetchError={addFetchError ?? convertToError(PagedAdminUsersError)}
          addFetchStatus={addFetchStatus}
          affectedOrders={affectedOrders || 0}
          getAffectedOrders={getAffectedOrders}
          affectedOrderFetchStatus={affectedOrderFetchStatus}
          onSelectAdmin={onSelectAdmin}
        />
      )}
      {!!editContact && (
        <EditDefaultContactForm
          clientId={clientId}
          onSubmit={onAddUpdateDefaultContact}
          onCancel={onCancelEditContactForm}
          getAffectedOrders={getAffectedOrders}
          initialData={editContact}
          deleteFetchError={deleteFetchError}
          deleteFetchStatus={deleteFetchStatus}
          updateFetchError={updateFetchError}
          updateFetchStatus={updateFetchStatus}
          affectedOrders={affectedOrders || 0}
          affectedOrderFetchStatus={affectedOrderFetchStatus}
        />
      )}
    </Card>
  );
};
